<template>
  <div class="legend" v-show="showLegend">
    <div class="legend_title">
      <span class="legend_title--prefix"></span>
      <span class="legend_title--content">
        图例
        <span v-if="selectedLayerName" class="legend_title--selected">
          点击图层：{{ selectedLayerName }}
        </span>
      </span>
      <img src="@/assets/myImg/1/icon-close.png" class="legend_title--suffix" @click="toggleLegend" alt="关闭图例" />
    </div>
    <div class="layers">
      <div class="layer" :class="{ 'layer-selected': pitchOn.includes(index) }" v-for="(item, index) in layers"
        :key="`layer-${item.index || index}`" @click="handleLayerClick(item, index)">
        <div class="layer-prefix">
          <!-- 图片类型样式 -->
          <img v-if="item.style?.styleType === 1" :src="item.style.styleValue" class="prefix_icon" :alt="item.name" />

          <!-- 线条类型样式 -->
          <div v-else-if="item.style?.styleType === 2" class="line-style">
            <div class="line-container">
              <svg style="width: 100%; height: 100%">
                <g fill="none" :stroke="getStrokeColor(item.style.styleValue)">
                  <path :stroke-width="getStrokeWidth(item.style.styleValue)" d="M0 15 l65 0"
                    :stroke-dasharray="getStrokeDashArray(item.style.styleValue)" />
                </g>
              </svg>
              <svg style="width: 100%; height: 50%" viewBox="-3 2 22 3">
                <g fill="none" :stroke="getStrokeColor(item.style.styleValue)">
                  <path d="M3,5 l1,-3 l1,3" :stroke-linejoin="getStrokeLineJoin(item.style.styleValue)" />
                </g>
              </svg>
            </div>
          </div>
          <div v-else-if="item.style?.styleType === 3" class="line-style">
            <div style="height: 20px; width: 20px; margin-left: 12px; margin-right: 12px"
              :style="useStyle(item.style.styleValue)"></div>
          </div>
        </div>

        <!-- 图层名称 -->
        <div class="layer-name">
          {{ item.name }}
        </div>

        <!-- 选中状态的单选按钮 -->
        <div v-if="pitchOn.includes(index)" class="layer-control">
          <input type="radio" :id="`layer-radio-${index}`" :checked="selectedID === index"
            :disabled="forbiddenList.includes(item.name)" @click.stop="toggleLayerVisibility(item, index)"
            class="layer-radio" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'LegendMap',
  props: {
    // 图例编码
    legendCode: {
      type: String,
      required: true
    },
    // 禁用点击的图层列表
    forbiddenList: {
      type: Array,
      default: () => ['物探点', '市政压力管线', '中压管线', '高压管线', '供水范围', '水厂范围', '管材管径']
    },
    // 默认选中的图层
    defaultPitchOn: {
      type: Array,
      default: () => ['管线', '供水范围', '水厂范围']
    },
    // API 接口地址（可选，默认使用标准接口）
    apiUrl: {
      type: String,
      default: '/manager/base-legend/detail'
    }
  },
  data () {
    return {
      layersRule: [], // 图例规则
      layers: [], // 图例
      showLegend: true,
      loading: false,
      pitchOn: [], // 选中图层
      selectedID: '', // 选中的图层ID
    }
  },
  computed: {
    // 获取当前选中的图层名称
    selectedLayerName () {
      if (this.selectedID !== null && this.layers[this.selectedID]) {
        return this.layers[this.selectedID].name
      }
      return null
    }
  },
  mounted () {
    this.getLayers()
  },

  watch: {
    // 监听图层数据变化，设置默认选中
    layers: {
      handler (newLayers) {
        this.setDefaultPitchOn()
      },
      immediate: true
    },
    selectedID: {
      handler (newSelectedID) {
        this.$emit('selected-id-change', {
          selectedID: newSelectedID,
          item: newSelectedID !== null ? this.layers[newSelectedID] : null
        })
      }
    },
    // 监听 legendCode 变化，重新获取图层数据
    legendCode: {
      handler (newCode, oldCode) {
        if (newCode !== oldCode && oldCode !== undefined) {
          this.getLayers()
        }
      }
    }
  },
  methods: {
    handleShowLegend () {
      console.log('图例');
      this.showLegend = !this.showLegend
    },
    // 设置默认选中图层
    setDefaultPitchOn () {
      if (!this.layers || this.layers.length === 0) return

      // 根据图层名称找到对应的索引（保持 defaultPitchOn 的顺序）
      const defaultIndices = []
      const layerIndexMap = {}

      // 先建立图层名称到索引的映射
      this.layers.forEach((layer, index) => {
        layerIndexMap[layer.name] = index
      })

      // 按照 defaultPitchOn 数组的顺序来添加索引
      this.defaultPitchOn.forEach(layerName => {
        if (layerIndexMap.hasOwnProperty(layerName)) {
          defaultIndices.push(layerIndexMap[layerName])
        }
      })

      // 设置默认选中
      this.pitchOn = defaultIndices

      // 如果有默认选中的图层
      if (defaultIndices.length > 0) {
        // 按照 defaultPitchOn 数组的顺序，找到第一个不在 forbiddenList 中的图层作为 selectedID
        let firstSelectableIndex = null
        for (let i = 0; i < this.defaultPitchOn.length; i++) {
          const layerName = this.defaultPitchOn[i]
          const layerIndex = layerIndexMap[layerName]
          if (layerIndex !== undefined && !this.forbiddenList.includes(layerName)) {
            firstSelectableIndex = layerIndex
            break
          }
        }

        // 如果找到了可选中的图层，设置为 selectedID
        if (firstSelectableIndex !== null) {
          this.selectedID = firstSelectableIndex
          this.$set(this.layers[firstSelectableIndex], 'visible', true)
        } else {
          // 如果所有默认图层都在 forbiddenList 中，仍然设置第一个，但不设置 visible
          // 这样图层仍然会被加载，只是不能通过单选按钮控制
          this.selectedID = defaultIndices[0]
        }

        // 通知父组件所有默认选中图层的状态（包括 forbiddenList 中的图层）
        defaultIndices.forEach((index) => {
          this.$emit('layer-click', {
            item: this.layers[index],
            index: index,
            selected: true
          })
        })
      }
    },

    // 获取图层数据
    async getLayers () {
      try {
        this.loading = true
        const res = await this.$http.get(`${this.apiUrl}?legendCode=${this.legendCode}`)

        if (res.data && res.data.layerList) {
          this.layersRule = res.data.layerList
          this.layers = this.layersRule.map((item, index) => ({
            index: index,
            id: item.id,
            name: item.layerName || `图层${index + 1}`,
            style: item.style || { styleType: 1, styleValue: '' },
            dataSource: item.dataSource,
            workspace: item.workspace,
            layerShowSort: item.layerShowSort,
            showFilterCondition: item.showFilterCondition,
            visible: true // 默认可见
          }))
        }
      } catch (error) {
        console.error('获取图例数据失败:', error)
        this.$message?.error('获取图例数据失败')
      } finally {
        this.loading = false
      }
    },

    // 切换图例显示
    toggleLegend () {
      this.showLegend = !this.showLegend
      this.$emit('legend-toggle', this.showLegend)
    },

    // 处理图层点击
    handleLayerClick (item, index) {
      const wasSelected = this.pitchOn.includes(index)
      
      // 切换图层选中状态
      if (wasSelected) {
        if (this.selectedRadio === index) {
          this.selectedRadio = null
          this.$set(item, 'visible', false)
        }
        // 如果已选中，则取消选中
        this.pitchOn = this.pitchOn.filter(i => i !== index)
        // 如果取消选中的图层就是当前点击图层，则清除selectedID
        if (this.selectedID === index) {
          this.selectedID = null
        }
      } else {
        // 如果未选中，则添加到选中列表
        this.pitchOn.push(index)
        // 只有当图层被添加到pitchOn列表中时，才设置为点击图层（除非是禁止的图层）
        if (!this.forbiddenList.includes(item.name)) {
          this.selectedID = index
        }
        
        // 当打开属性点开关时，触发地图缩放（显示100米比例尺）
        this.$emit('scale-to-ratio', {
          ratio: 100,
          item,
          index
        })
      }

      // 发送事件给父组件
      this.$emit('layer-click', {
        item,
        index,
        selected: this.pitchOn.includes(index)
      })
    },

    // 切换图层可见性
    toggleLayerVisibility (item, index) {
      // 单选按钮互斥：只能有一个被选中
      if (this.selectedID === index) {
        // 如果已选中，则取消选中（隐藏图层）
        this.selectedID = null
        this.$set(item, 'visible', false)
      } else {
        // 如果未选中，则选中当前，取消其他
        this.selectedID = index
        this.$set(item, 'visible', true)

        // 取消其他图层的选中状态
        this.layers.forEach((layer, i) => {
          if (i !== index) {
            this.$set(layer, 'visible', false)
          }
        })
        
        // 当显示图层时，触发地图缩放（显示100米比例尺）
        this.$emit('scale-to-ratio', {
          ratio: 100,
          item,
          index
        })
      }

      // 发送 selectedID 变化事件
      this.$emit('selected-id-change', {
        selectedID: this.selectedID,
        item: this.selectedID !== null ? this.layers[this.selectedID] : null
      })
    },

    // 解析样式值 - 获取背景色和其他样式
    getLineStyle (styleValue) {
      if (!styleValue) return {}
      const arr = styleValue.split(';')
      const styles = {}

      arr.forEach(style => {
        const [key, value] = style.split(':')
        if (key && value) {
          switch (key.trim()) {
            case 'background':
              styles.backgroundColor = value
              break
            case 'opacity':
              styles.opacity = value
              break
            case 'border':
              styles.border = value
              break
          }
        }
      })

      return styles
    },

    // 获取描边颜色 - 对应 useStyle1 过滤器
    getStrokeColor (styleValue) {
      return this.useStyle1(styleValue)
    },

    // 获取描边宽度 - 对应 useStyle2 过滤器
    getStrokeWidth (styleValue) {
      return this.useStyle2(styleValue)
    },

    // 获取虚线样式 - 对应 useStyle3 过滤器
    getStrokeDashArray (styleValue) {
      return this.useStyle3(styleValue)
    },

    // 获取线条连接方式 - 对应 useStyle4 过滤器
    getStrokeLineJoin (styleValue) {
      return this.useStyle4(styleValue)
    },

    // useStyle1 过滤器 - 获取描边颜色
    useStyle1 (value) {
      if (!value) return '#000000'
      const arr = value.split(';')
      const arrs = []
      for (let i = 0; i < arr.length; i++) {
        arrs.push(arr[i].split(':')[1])
      }
      return arrs[1] || '#000000'
    },

    // useStyle2 过滤器 - 获取描边宽度
    useStyle2 (value) {
      if (!value) return '1'
      const arr = value.split(';')
      const arrs = []
      for (let i = 0; i < arr.length; i++) {
        arrs.push(arr[i].split(':')[1])
      }
      return arrs[0] || '1'
    },

    // useStyle3 过滤器 - 获取虚线样式
    useStyle3 (value) {
      if (!value) return 'none'
      const arr = value.split(';')
      const arrs = []
      for (let i = 0; i < arr.length; i++) {
        arrs.push(arr[i].split(':')[1])
      }
      return arrs[2] || 'none'
    },

    // useStyle4 过滤器 - 获取线条连接方式
    useStyle4 (value) {
      if (!value) return 'miter'
      const arr = value.split(';')
      const arrs = []
      for (let i = 0; i < arr.length; i++) {
        arrs.push(arr[i].split(':')[1])
      }
      return arrs[3] || 'miter'
    },

    // useStyle 过滤器 - 用于 styleType === 3 的样式
    useStyle (value) {
      if (!value) return {}
      const arr = value.split(';')
      const arrs = []
      for (let i = 0; i < arr.length; i++) {
        arrs.push(arr[i].split(':')[1])
      }

      // 根据参考代码的 useStyle 过滤器逻辑
      const style = {}
      if (arrs[0]) style.backgroundColor = arrs[0]
      if (arrs[1]) style.borderColor = arrs[1]
      if (arrs[2]) style.borderStyle = arrs[2]
      if (arrs[3]) style.borderWidth = arrs[3] + 'px'

      return style
    }
  }
}
</script>
<style lang="less" scoped>
.legend {
  position: absolute;
  background: rgba(0, 32, 54, 0.75);
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  bottom: 24px;
  right: 24px;
  z-index: 5;
  width: 240px;
  max-height: 400px;
  overflow: hidden;
}

.legend_title {
  background-image: linear-gradient(270deg, rgba(7, 142, 231, 0.959) 0%, #0890f2 100%);
  border-radius: 6px 6px 0 0;
  display: flex;
  height: 46px;
  align-items: center;
  color: #ffffff;
  cursor: pointer;
  user-select: none;

  .legend_title--prefix {
    width: 4px;
    height: 12px;
    background-color: #0890f2;
    margin-right: 5px;
    margin-left: 12px;
    border-radius: 2px;
  }

  .legend_title--content {
    flex: 1;
    font-family: 'Microsoft YaHei', sans-serif;
    font-size: 12px;
    color: #ffffff;
    font-weight: 600;
    line-height: 12px;
  }

  .legend_title--selected {
    font-size: 12px;
    font-weight: 600;
    margin-left: 4px;
  }

  .legend_title--suffix {
    width: 12px;
    height: 12px;
    margin-right: 12px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }
}

.layers {
  width: 100%;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0;

  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.layer {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &.layer-selected {
    background-color: rgba(8, 144, 242, 0.2);
    border-left: 3px solid #0890f2;
  }

  &:last-child {
    border-bottom: none;
  }
}

.layer-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  min-width: 44px;
  margin-right: 12px;

  .prefix_icon {
    height: 20px;
    width: 20px;
    object-fit: contain;
  }

  .line-style {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 22px;
  }

  .line-container {
    width: 20px;
    height: 20px;
    margin-left: 12px;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
}

.layer-name {
  flex: 1;
  font-size: 12px;
  color: #ffffff;
  font-family: 'Microsoft YaHei', sans-serif;
  line-height: 1.4;
  word-break: break-all;
}

.layer-control {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.layer-radio {
  margin-right: 4px;
  cursor: pointer;
}

.layer-radio-label {
  font-size: 12px;
  color: #ffffff;
  cursor: pointer;
  user-select: none;
}

/* 加载状态 */
.legend.loading {
  .layers {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    color: #ffffff;
    font-size: 12px;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .legend {
    width: 200px;
    bottom: 16px;
    right: 16px;
  }

  .legend_title {
    height: 32px;

    .legend_title--content {
      font-size: 11px;
    }
  }

  .layer {
    padding: 6px 8px;
  }

  .layer-name {
    font-size: 11px;
  }
}
</style>
